import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Send, Loader2, UserPlus } from "lucide-react";

/**
 * AssignmentDialog - Admin dialog for assigning training to staff
 * Allows selection of staff member, discipline track, and due date
 */
export default function AssignmentDialog({
  isOpen,
  onClose,
  staffList = [],
  disciplines = [],
  onCreateAssignment,
  isLoading = false
}) {
  const [selectedStaff, setSelectedStaff] = useState("");
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");

  const handleSubmit = async () => {
    await onCreateAssignment?.({
      userId: selectedStaff,
      discipline: selectedDiscipline,
      dueDate: dueDate || null,
      notes: notes || null
    });
    
    // Reset form
    setSelectedStaff("");
    setSelectedDiscipline("");
    setDueDate("");
    setNotes("");
  };

  const handleClose = () => {
    setSelectedStaff("");
    setSelectedDiscipline("");
    setDueDate("");
    setNotes("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Assign Training
          </DialogTitle>
          <DialogDescription>
            Assign discipline-specific training to a staff member. They will receive an email notification.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Staff Selection */}
          <div className="space-y-2">
            <Label>Staff Member</Label>
            <Select value={selectedStaff} onValueChange={setSelectedStaff}>
              <SelectTrigger>
                <SelectValue placeholder="Select staff member..." />
              </SelectTrigger>
              <SelectContent>
                {staffList.map(staff => (
                  <SelectItem key={staff.id} value={staff.id}>
                    {staff.name} - {staff.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Discipline Selection */}
          <div className="space-y-2">
            <Label>Training Track</Label>
            <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
              <SelectTrigger>
                <SelectValue placeholder="Select training track..." />
              </SelectTrigger>
              <SelectContent>
                {disciplines.map(disc => (
                  <SelectItem key={disc.key} value={disc.key}>
                    {disc.title} ({disc.totalCEUs} CEUs)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Due Date (Optional)</Label>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes (Optional)</Label>
            <Textarea
              placeholder="Add any notes about this assignment..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!selectedStaff || !selectedDiscipline || isLoading}
            className="gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Assigning...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Assign Training
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
